import React, { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { getAuthToken } from "../../utils/authHelpers";
import { displayHandle } from "../../utils/streamHelpers";
import IntroVideoPreview from "../IntroVideoPreview";

/**
 * ApplicantCard — the host's smart triage card for an incoming application.
 *
 * Renders:
 *   - Avatar + handle + intro video thumbnail (hover-play, expandable)
 *   - Stats row: completed collabs, avg rating, tagged hosts, account age
 *   - AI vibe-check paragraph (1-2 sentences, "AI summary" badge)
 *     OR deterministic fallback sentence (no badge), depending on source
 *   - 4-button action row: Open chat / Start collab / Add to Waitlist / Decline
 *
 * The vibe-check is fetched lazily on first paint; while loading the host
 * still sees stats + action row so nothing is gated by an LLM call.
 */
const ApplicantCard = ({
  request,
  onOpenChat,
  onStartCollab,
  onWaitlist,
  onDecline,
  onDelete,
  showProperty = true,
}) => {
  const applicant = request?.otherUser;
  const snapshot = request?.snapshot || {};
  const applicationMessage = snapshot.applicationMessage || '';

  const [vibeCheck, setVibeCheck] = useState(null);
  const [vibeCheckError, setVibeCheckError] = useState(null);
  const [busyAction, setBusyAction] = useState(null); // 'chat' | 'collab' | 'waitlist' | 'decline' | null

  useEffect(() => {
    if (!applicant?.userId) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const token = getAuthToken();
        if (!token) return;
        const apiBase = window.location.hostname === "localhost" ? "http://localhost:4000" : "";
        const res = await fetch(
          `${apiBase}/api/collab/applications/vibe-check?requesterId=${encodeURIComponent(applicant.userId)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok || !data?.ok) {
          setVibeCheckError(data?.error || `http_${res.status}`);
          return;
        }
        setVibeCheck(data);
      } catch (err) {
        if (!cancelled) setVibeCheckError(err?.message || 'network');
      }
    })();

    return () => { cancelled = true; };
  }, [applicant?.userId]);

  const handle = applicant?.handle;
  const safeHandle = handle ? displayHandle(handle) : (applicant?.firstName || 'Applicant');

  // Status-aware labels so the same component reads correctly in every Inbox
  // section. "Open chat" is misleading when the chat already exists.
  const status = request?.status || 'pending';
  const openChatLabel = status === 'chatting' ? 'Continue chat' : 'Reply';
  const openChatBusyLabel = status === 'chatting' ? 'Opening…' : 'Opening…';
  const openChatTip = status === 'chatting'
    ? 'Jump back into your chat with this applicant.'
    : 'Open a chat to talk with this applicant.';

  // A small status hint under the handle so the host knows what state the
  // applicant is in without reading the section title every time.
  let statusHint = null;
  if (status === 'chatting' && request?.chatOpenedAt) {
    statusHint = `Chatting since ${new Date(request.chatOpenedAt).toLocaleDateString()}`;
  } else if (status === 'waitlisted' && request?.waitlistedAt) {
    const waitlistedDays = Math.max(0, Math.floor(
      (Date.now() - new Date(request.waitlistedAt).getTime()) / (1000 * 60 * 60 * 24)
    ));
    statusHint = waitlistedDays === 0
      ? 'Waitlisted today'
      : `Waitlisted ${waitlistedDays} day${waitlistedDays === 1 ? '' : 's'} ago`;
  }

  // Response-window countdown (PRD 1). Only meaningful while the application
  // is still pending — once the host acts, the clock stops mattering.
  let expiryChip = null;
  if (status === 'pending' && request?.expiresAt) {
    const daysLeft = Math.ceil(
      (new Date(request.expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
    );
    if (daysLeft > 0) {
      expiryChip = { label: `Expires in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`, urgent: daysLeft <= 5 };
    } else {
      expiryChip = { label: 'Expires today', urgent: true };
    }
  }

  // One-time creator nudge marker (PRD 2) — informational, never alarming.
  const wasNudged = !!request?.nudgedAt;

  const runAction = async (key, fn) => {
    if (busyAction) return;
    setBusyAction(key);
    try {
      await fn?.(request);
    } finally {
      setBusyAction(null);
    }
  };

  const hasProperty = showProperty && (request?.property?.imageUrl || request?.property?.name);

  return (
    <div className="applicant-card">
      <div className="applicant-card-header">
        {/* Pairing visual: applicant on the left, the thing they want to
            collaborate with on the right, joined by a clear connector so the
            host instantly reads "X wants to collaborate with Y". */}
        <div className={`applicant-card-pairing${hasProperty ? '' : ' solo'}`}>
          <div className="applicant-card-party">
            <div className="applicant-card-photo">
              {applicant?.introVideoPublicId || applicant?.introVideoUrl ? (
                <IntroVideoPreview
                  mode="card"
                  publicId={applicant.introVideoPublicId}
                  posterUrl={applicant.introVideoPosterUrl}
                  fullUrl={applicant.introVideoUrl}
                  fallbackImageUrl={applicant.avatarUrl}
                  alt={`${safeHandle} intro video`}
                />
              ) : applicant?.avatarUrl ? (
                <img src={applicant.avatarUrl} alt={safeHandle} className="applicant-card-photo-img" />
              ) : (
                <div className="applicant-card-photo-img applicant-card-photo-placeholder">
                  {(safeHandle || '?').charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            <div className="applicant-card-party-name">{safeHandle}</div>
            <SocialIcons applicant={applicant} />
          </div>

          {hasProperty && (
            <>
              <div className="applicant-card-connector">
                <span className="applicant-card-connector-label">wants to collaborate with</span>
                <span className="applicant-card-connector-arrow" aria-hidden>→</span>
              </div>

              <div className="applicant-card-party">
                <div className="applicant-card-photo">
                  {request.property.imageUrl ? (
                    <img
                      src={request.property.imageUrl}
                      alt={request.property.name || 'Property'}
                      className="applicant-card-photo-img"
                    />
                  ) : (
                    <div className="applicant-card-photo-img applicant-card-photo-placeholder">
                      {(request.property.name || '?').charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                {request.property.name && (
                  <div className="applicant-card-party-name">{request.property.name}</div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="applicant-card-sub">
          Applied {request?.createdAt ? new Date(request.createdAt).toLocaleDateString() : ''}
        </div>
        {(statusHint || expiryChip || wasNudged) && (
          <div className="applicant-card-chips">
            {statusHint && <span className="applicant-card-status-hint">{statusHint}</span>}
            {expiryChip && (
              <span
                className={`applicant-card-expiry-chip${expiryChip.urgent ? ' urgent' : ''}`}
                title="Applications are good for 14 days — after that the creator is invited to reapply."
              >
                {expiryChip.label}
              </span>
            )}
            {wasNudged && (
              <Tippy content="This creator sent a friendly reminder about their application.">
                <span className="applicant-card-nudged-chip">Nudged</span>
              </Tippy>
            )}
          </div>
        )}
      </div>

      {/* Stats row (always rendered from deterministic SQL; no LLM dependency) */}
      <StatsRow stats={vibeCheck?.stats} loading={!vibeCheck && !vibeCheckError} />

      {/* Vibe-check paragraph */}
      <VibeCheckBlock vibeCheck={vibeCheck} error={vibeCheckError} />

      {/* The applicant's own note (if any). Kept visually separate from the
          AI summary so the host can tell verified facts from self-pitch. */}
      {applicationMessage && (
        <div className="applicant-card-message">
          <div className="applicant-card-message-label">Note from {safeHandle}</div>
          <p className="applicant-card-message-text">{applicationMessage}</p>
        </div>
      )}

      {/* The host's own waitlist note (PRD 3) — a reminder of why they
          parked this applicant. */}
      {status === 'waitlisted' && request?.statusNote && (
        <div className="applicant-card-message">
          <div className="applicant-card-message-label">Your note</div>
          <p className="applicant-card-message-text">{request.statusNote}</p>
        </div>
      )}

      {/* Actions auto-hide when their handler is null, so the same component
          fits "New", "In conversation", and "Waitlisted" sections without a
          ton of variant code. The delete trash icon (when provided) sits
          right-aligned outside the main button grid. */}
      <div className="applicant-card-actions-row">
      <div className={`applicant-card-actions actions-count-${
        [onOpenChat, onStartCollab, onWaitlist, onDecline].filter(Boolean).length
      }`}>
        {onOpenChat && (
          <Tippy content={openChatTip}>
            <button
              type="button"
              className="applicant-card-btn primary"
              disabled={!!busyAction}
              onClick={() => runAction('chat', onOpenChat)}
            >
              {busyAction === 'chat' ? openChatBusyLabel : openChatLabel}
            </button>
          </Tippy>
        )}
        {onStartCollab && (
          <Tippy content="Accept and start the collab workspace together.">
            <button
              type="button"
              className="applicant-card-btn secondary"
              disabled={!!busyAction}
              onClick={() => runAction('collab', onStartCollab)}
            >
              {busyAction === 'collab' ? 'Starting…' : 'Initiate collab'}
            </button>
          </Tippy>
        )}
        {onWaitlist && (
          <Tippy content="Save them for later without replying yet. You'll find them anytime in your Waitlist tab.">
            <button
              type="button"
              className="applicant-card-btn tertiary"
              disabled={!!busyAction}
              onClick={() => runAction('waitlist', onWaitlist)}
            >
              {busyAction === 'waitlist' ? 'Adding…' : 'Add to waitlist'}
            </button>
          </Tippy>
        )}
        {onDecline && (
          <Tippy content={status === 'waitlisted'
            ? "Release them from your waitlist. They'll be gently notified."
            : "Pass on this application. They'll be notified."}>
            <button
              type="button"
              className="applicant-card-btn ghost"
              disabled={!!busyAction}
              onClick={() => runAction('decline', onDecline)}
            >
              {busyAction === 'decline'
                ? (status === 'waitlisted' ? 'Removing…' : 'Declining…')
                : (status === 'waitlisted' ? 'Remove from waitlist' : 'Decline collab')}
            </button>
          </Tippy>
        )}
      </div>
      {onDelete && (
        <Tippy content="Delete this request permanently. This cannot be undone.">
          <button
            type="button"
            className="applicant-card-delete"
            disabled={!!busyAction}
            onClick={() => onDelete(request)}
            aria-label="Delete request permanently"
          >
            <Trash2 size={16} />
          </button>
        </Tippy>
      )}
      </div>

      <ApplicantCardStyles />
    </div>
  );
};

/**
 * Small clickable social icons for the applicant. Lets a host pop the
 * creator's Instagram / TikTok / YouTube in a new tab without leaving the
 * inbox. Links + icons mirror the public creator profile; nothing renders
 * when the applicant has no socials on file.
 */
const normalizeSocialHref = (platform, value) => {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;

  const handle = raw
    .replace(/^@/, '')
    .replace(/^instagram\.com\//i, '')
    .replace(/^tiktok\.com\/@?/i, '')
    .replace(/^youtube\.com\/@?/i, '')
    .replace(/^youtu\.be\//i, '')
    .split(/[/?#&]/)[0]
    .trim();

  if (!handle) return null;
  if (platform === 'instagram') return `https://instagram.com/${handle}`;
  if (platform === 'tiktok') return `https://tiktok.com/@${handle}`;
  if (platform === 'youtube') return `https://youtube.com/@${handle}`;
  return raw;
};

const SocialIcons = ({ applicant }) => {
  const links = [
    {
      key: 'instagram',
      url: normalizeSocialHref('instagram', applicant?.instagramUrl),
      label: 'Instagram',
      viewBox: '0 0 24 24',
      path: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1 1 12.324 0 6.162 6.162 0 0 1-12.324 0zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm4.965-10.405a1.44 1.44 0 1 1 2.881.001 1.44 1.44 0 0 1-2.881-.001z',
    },
    {
      key: 'tiktok',
      url: normalizeSocialHref('tiktok', applicant?.tiktokUrl),
      label: 'TikTok',
      viewBox: '0 0 32 32',
      path: 'M16.656 1.029c1.637-0.025 3.262-0.012 4.886-0.025 0.054 2.031 0.878 3.859 2.189 5.213l-0.002-0.002c1.411 1.271 3.247 2.095 5.271 2.235l0.028 0.002v5.036c-1.912-0.048-3.71-0.489-5.331-1.247l0.082 0.034c-0.784-0.377-1.447-0.764-2.077-1.196l0.052 0.034c-0.012 3.649 0.012 7.298-0.025 10.934-0.103 1.853-0.719 3.543-1.707 4.954l0.020-0.031c-1.652 2.366-4.328 3.919-7.371 4.011l-0.014 0c-0.123 0.006-0.268 0.009-0.414 0.009-1.73 0-3.347-0.482-4.725-1.319l0.040 0.023c-2.508-1.509-4.238-4.091-4.558-7.094l-0.004-0.041c-0.025-0.625-0.037-1.25-0.012-1.862 0.49-4.779 4.494-8.476 9.361-8.476 0.547 0 1.083 0.047 1.604 0.136l-0.056-0.008c0.025 1.849-0.050 3.699-0.050 5.548-0.423-0.153-0.911-0.242-1.42-0.242-1.868 0-3.457 1.194-4.045 2.861l-0.009 0.030c-0.133 0.427-0.21 0.918-0.21 1.426 0 0.206 0.013 0.41 0.037 0.61l-0.002-0.024c0.332 2.046 2.086 3.59 4.201 3.59 0.061 0 0.121-0.001 0.181-0.004l-0.009 0c1.463-0.044 2.733-0.831 3.451-1.994l0.010-0.018c0.267-0.372 0.45-0.822 0.511-1.311l0.001-0.014c0.125-2.237 0.075-4.461 0.087-6.698 0.012-5.036-0.012-10.060 0.025-15.083z',
    },
    {
      key: 'youtube',
      url: normalizeSocialHref('youtube', applicant?.youtubeUrl),
      label: 'YouTube',
      viewBox: '0 0 20 14',
      path: 'M19.615 3.154c-.19-.84-.75-1.5-1.577-1.692C16.264 1.1 10 .9 10 .9S3.736 1.1 2.962 1.462c-.827.192-1.387.852-1.577 1.692C1.1 5.03 1 6.5 1 6.5s.1 1.47.385 3.346c.19.84.75 1.5 1.577 1.692C3.736 11.9 10 12.1 10 12.1s6.264-.2 7.038-.562c.827-.192 1.387-.852 1.577-1.692C18.9 7.97 19 6.5 19 6.5s-.1-1.47-.385-3.346zM8.182 9.182V3.818L13.182 6.5l-5 2.682z',
    },
  ].filter((l) => l.url);

  if (links.length === 0) return null;

  return (
    <div className="applicant-card-socials">
      {links.map((l) => (
        <a
          key={l.key}
          href={l.url}
          target="_blank"
          rel="noopener noreferrer"
          className="applicant-card-social"
          aria-label={`${l.label} (opens in a new tab)`}
          title={l.label}
          onClick={(e) => e.stopPropagation()}
        >
          <svg width="18" height="18" viewBox={l.viewBox} fill="currentColor" aria-hidden>
            <path d={l.path} />
          </svg>
        </a>
      ))}
    </div>
  );
};

const StatsRow = ({ stats, loading }) => {
  if (loading) {
    return (
      <div className="applicant-card-stats applicant-card-stats-loading" aria-hidden>
        <div className="applicant-card-stat-skeleton" />
        <div className="applicant-card-stat-skeleton" />
        <div className="applicant-card-stat-skeleton" />
      </div>
    );
  }
  if (!stats) return null;
  const reviewLabel = stats.reviewCount > 0
    ? `${stats.avgRating ?? '—'}★ · ${stats.reviewCount} review${stats.reviewCount === 1 ? '' : 's'}`
    : 'No reviews yet';
  const taggedLabel = stats.taggedHosts?.length > 0
    ? `${stats.taggedHosts.length} tagged host${stats.taggedHosts.length === 1 ? '' : 's'}`
    : 'Not yet tagged';
  return (
    <div className="applicant-card-stats">
      <Stat label="Completed" value={stats.completedCollabs} />
      <Stat label="Reviews" value={reviewLabel} />
      <Stat label="Network" value={taggedLabel} />
      <Stat label="Joined" value={stats.accountAgeMonths ? `${stats.accountAgeMonths}mo ago` : 'New'} />
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="applicant-card-stat">
    <div className="applicant-card-stat-label">{label}</div>
    <div className="applicant-card-stat-value">{value}</div>
  </div>
);

const VibeCheckBlock = ({ vibeCheck, error }) => {
  if (error) {
    return (
      <div className="applicant-card-vibe">
        <div className="applicant-card-vibe-label">The quick read</div>
        <p className="applicant-card-vibe-text">
          Couldn't load the summary right now — the stats above are still good.
        </p>
      </div>
    );
  }
  if (!vibeCheck) {
    return (
      <div className="applicant-card-vibe applicant-card-vibe-loading">
        <div className="applicant-card-vibe-label">The quick read</div>
        <div className="applicant-card-vibe-skeleton" />
        <div className="applicant-card-vibe-skeleton short" />
      </div>
    );
  }
  const isAi = vibeCheck.source === 'ai';
  const priorCollabs = (vibeCheck.stats?.priorCollabs || []).filter((c) => c?.slug || c?.id);
  return (
    <div className="applicant-card-vibe">
      <div className="applicant-card-vibe-label">
        The quick read
        {isAi && <span className="applicant-card-vibe-badge" title={`Grounded summary (model: ${vibeCheck.model || 'unknown'})`}>AI</span>}
      </div>
      <p className="applicant-card-vibe-text">{vibeCheck.summary}</p>
      {priorCollabs.length > 0 && (
        <div className="applicant-card-prior-collabs">
          <div className="applicant-card-prior-collabs-label">Prior collabs</div>
          <div className="applicant-card-prior-collabs-list">
            {priorCollabs.map((c) => (
              <a
                key={c.slug || c.id}
                href={`/c/p/${encodeURIComponent(c.slug || c.id)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="applicant-card-prior-collab"
                title={`View ${c.name} (opens in a new tab)`}
                onClick={(e) => e.stopPropagation()}
              >
                {c.name}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ApplicantCardStyles = () => (
  <style>{`
    .applicant-card {
      background: #ffffff;
      border: 1px solid #e8e6f4;
      border-radius: 14px;
      padding: 18px 18px 16px;
      margin-bottom: 14px;
      box-shadow:
        0 1px 2px rgba(15, 8, 75, 0.05),
        0 4px 14px rgba(15, 8, 75, 0.07),
        0 12px 32px rgba(15, 8, 75, 0.06);
    }
    .applicant-card-header {
      margin-bottom: 4px;
      text-align: center;
    }
    .applicant-card-header:has(.applicant-card-pairing.solo) {
      text-align: left;
    }
    .applicant-card-pairing {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 14px;
      padding: 4px 0 12px;
    }
    .applicant-card-pairing.solo {
      justify-content: flex-start;
      gap: 14px;
    }
    .applicant-card-party {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      max-width: 180px;
    }
    .applicant-card-pairing.solo .applicant-card-party {
      flex-direction: row;
      align-items: center;
      gap: 14px;
      max-width: none;
      text-align: left;
    }
    .applicant-card-photo {
      width: 116px;
      height: 116px;
      border-radius: 16px;
      overflow: hidden;
      background: #f4f3fb;
      border: 1px solid #e8e6f4;
      flex-shrink: 0;
    }
    .applicant-card-photo-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      background: #f4f3fb;
      display: block;
    }
    .applicant-card-photo-placeholder {
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: 'Sailors', serif;
      font-size: 40px;
      color: #0f084b;
    }
    .applicant-card-party-name {
      font-family: 'Sailors', serif;
      font-size: 18px;
      color: #0f084b;
      line-height: 1.15;
      text-align: center;
      word-break: break-word;
    }
    .applicant-card-pairing.solo .applicant-card-party-name {
      text-align: left;
      font-size: 20px;
    }
    .applicant-card-socials {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      margin-top: 2px;
    }
    .applicant-card-pairing.solo .applicant-card-socials {
      justify-content: flex-start;
    }
    .applicant-card-social {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: #6b7280;
      transition: color 0.15s, transform 0.15s;
    }
    .applicant-card-social:hover {
      color: #0f084b;
      transform: translateY(-1px);
    }
    .applicant-card-connector {
      flex-shrink: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      max-width: 120px;
    }
    .applicant-card-connector-label {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: #846a16;
      text-align: center;
      line-height: 1.25;
    }
    .applicant-card-connector-arrow {
      font-size: 22px;
      color: #dc1d08;
      line-height: 1;
    }
    @media (max-width: 480px) {
      .applicant-card-photo { width: 92px; height: 92px; border-radius: 14px; }
      .applicant-card-party-name { font-size: 16px; }
      .applicant-card-connector { max-width: 84px; gap: 2px; }
      .applicant-card-connector-label { font-size: 9.5px; letter-spacing: 0.04em; }
      .applicant-card-pairing { gap: 8px; }
    }
    .applicant-card-sub {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 12px;
      color: #6b7280;
      margin-top: 2px;
      text-align: center;
    }
    .applicant-card-pairing.solo + .applicant-card-sub {
      text-align: left;
    }
    .applicant-card-chips {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-wrap: wrap;
      gap: 6px;
      margin-top: 6px;
    }
    .applicant-card-header:has(.applicant-card-pairing.solo) .applicant-card-chips {
      justify-content: flex-start;
    }
    .applicant-card-status-hint {
      display: inline-block;
      padding: 2px 8px;
      background: #f4f3fb;
      color: #4a4174;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.02em;
      border-radius: 100px;
    }
    .applicant-card-expiry-chip {
      display: inline-block;
      padding: 2px 8px;
      background: #f3f4f6;
      color: #666666;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.02em;
      border-radius: 100px;
    }
    .applicant-card-expiry-chip.urgent {
      background: rgba(220, 29, 8, 0.1);
      color: #dc1d08;
    }
    .applicant-card-nudged-chip {
      display: inline-block;
      padding: 1px 8px;
      background: #ffffff;
      color: #0f084b;
      border: 1px solid #0f084b;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.02em;
      border-radius: 100px;
    }
    .applicant-card-stats {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 8px;
      margin: 14px 0 10px;
    }
    @media (max-width: 540px) {
      .applicant-card-stats { grid-template-columns: repeat(2, 1fr); }
    }
    .applicant-card-stat {
      background: #f8f7fd;
      border-radius: 10px;
      padding: 8px 10px;
      min-width: 0;
    }
    .applicant-card-stat-label {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: #6b7280;
    }
    .applicant-card-stat-value {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 14px;
      font-weight: 600;
      color: #0f084b;
      margin-top: 2px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .applicant-card-stats-loading .applicant-card-stat-skeleton {
      height: 46px;
      background: linear-gradient(90deg, #f4f3fb 0%, #ece9f6 50%, #f4f3fb 100%);
      background-size: 200% 100%;
      animation: applicantSkeleton 1.4s linear infinite;
      border-radius: 10px;
    }
    @keyframes applicantSkeleton {
      0% { background-position: 0% 0; }
      100% { background-position: -200% 0; }
    }
    .applicant-card-vibe {
      background: #fdfbf6;
      border: 1px solid #f1ead8;
      border-radius: 14px;
      padding: 14px 16px 16px;
      margin: 4px 0 14px;
    }
    .applicant-card-vibe-label {
      display: flex;
      align-items: center;
      gap: 8px;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: #846a16;
    }
    .applicant-card-vibe-badge {
      background: #0f084b;
      color: #ffffff;
      font-size: 9px;
      letter-spacing: 0.08em;
      padding: 2px 6px;
      border-radius: 8px;
    }
    .applicant-card-vibe-text {
      margin: 10px 0 0;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 17px;
      line-height: 1.55;
      color: #1f2435;
      letter-spacing: 0.005em;
    }
    @media (max-width: 480px) {
      .applicant-card-vibe-text { font-size: 16px; line-height: 1.5; }
      .applicant-card-vibe { padding: 12px 14px 14px; }
    }
    .applicant-card-prior-collabs {
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px solid #f1ead8;
    }
    .applicant-card-prior-collabs-label {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: #846a16;
      margin-bottom: 8px;
    }
    .applicant-card-prior-collabs-list {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }
    .applicant-card-prior-collab {
      display: inline-flex;
      align-items: center;
      max-width: 100%;
      padding: 5px 12px;
      border-radius: 100px;
      background: #ffffff;
      border: 1px solid #e8e6f4;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 13px;
      font-weight: 600;
      color: #0f084b;
      text-decoration: none;
      transition: all 0.15s;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .applicant-card-prior-collab:hover {
      border-color: #0f084b;
      background: #f4f3fb;
    }
    .applicant-card-vibe-skeleton {
      height: 14px;
      width: 100%;
      margin-top: 10px;
      border-radius: 7px;
      background: linear-gradient(90deg, #f4f3fb 0%, #ece9f6 50%, #f4f3fb 100%);
      background-size: 200% 100%;
      animation: applicantSkeleton 1.4s linear infinite;
    }
    .applicant-card-vibe-skeleton.short { width: 60%; }
    .applicant-card-message {
      background: #ffffff;
      border: 1px solid #e8e6f4;
      border-radius: 12px;
      padding: 10px 12px;
      margin-bottom: 12px;
    }
    .applicant-card-message-label {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: #6b7280;
    }
    .applicant-card-message-text {
      margin: 8px 0 0;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 15px;
      line-height: 1.55;
      color: #353945;
      white-space: pre-wrap;
    }
    @media (max-width: 480px) {
      .applicant-card-message-text { font-size: 14.5px; }
    }
    .applicant-card-actions-row {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .applicant-card-actions {
      flex: 1;
      min-width: 0;
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 8px;
    }
    .applicant-card-delete {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      flex-shrink: 0;
      margin-left: auto;
      padding: 0;
      background: #ffffff;
      border: 1px solid #e8e6f4;
      border-radius: 10px;
      color: #6b7280;
      cursor: pointer;
      transition: all 0.15s;
    }
    .applicant-card-delete:hover:not(:disabled) {
      color: #dc1d08;
      border-color: #dc1d08;
      background: #fef2f2;
    }
    .applicant-card-delete:disabled { opacity: 0.5; cursor: default; }
    .applicant-card-actions.actions-count-1 { grid-template-columns: 1fr; }
    .applicant-card-actions.actions-count-2 { grid-template-columns: repeat(2, 1fr); }
    .applicant-card-actions.actions-count-3 { grid-template-columns: repeat(2, 1fr); }
    @media (min-width: 720px) {
      .applicant-card-actions.actions-count-3 { grid-template-columns: repeat(3, 1fr); }
      .applicant-card-actions.actions-count-4 { grid-template-columns: repeat(4, 1fr); }
    }
    .applicant-card-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 10px 14px;
      border-radius: 100px;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 13.5px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.15s;
      min-height: 40px;
      border: 1.5px solid transparent;
    }
    .applicant-card-btn:disabled { opacity: 0.6; cursor: default; }
    .applicant-card-btn.primary {
      background: #dc1d08;
      color: #ffffff;
      border-color: #dc1d08;
    }
    .applicant-card-btn.primary:hover:not(:disabled) { background: #b51706; border-color: #b51706; }
    .applicant-card-btn.secondary {
      background: #0f084b;
      color: #ffffff;
      border-color: #0f084b;
    }
    .applicant-card-btn.secondary:hover:not(:disabled) { background: #1a107a; border-color: #1a107a; }
    .applicant-card-btn.tertiary {
      background: #ffffff;
      color: #0f084b;
      border-color: #0f084b;
    }
    .applicant-card-btn.tertiary:hover:not(:disabled) { background: #f4f3fb; }
    .applicant-card-btn.ghost {
      background: #ffffff;
      color: #6b7280;
      border-color: #e8e6f4;
    }
    .applicant-card-btn.ghost:hover:not(:disabled) { color: #dc1d08; border-color: #dc1d08; }
  `}</style>
);

export default ApplicantCard;
